(() => {
  if (window.__velisseQuizScriptLoaded) return;
  window.__velisseQuizScriptLoaded = true;

  const STORAGE_KEY_PREFIX = 'velisseBeautyQuiz';
  const ANALYTICS_STORAGE_PREFIX = 'velisseQuizAnalytics';
  const MONTHS_UPPER = [
    'ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO',
    'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'
  ];
  const MONTH_NAMES = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
  ];

  function loadAnswers(key) {
    try {
      return JSON.parse(sessionStorage.getItem(key) || '{}');
    } catch (_error) {
      return {};
    }
  }

  function saveAnswers(key, answers) {
    try {
      sessionStorage.setItem(key, JSON.stringify(answers));
    } catch (_error) {
      // The quiz remains usable when storage is unavailable.
    }
  }

  function clearAnswers(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (_error) {
      // Nothing else is required.
    }
  }

  function createSessionId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  }

  function loadAnalyticsState(key) {
    try {
      const stored = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (stored?.sessionId) {
        return {
          sent: {},
          viewedSteps: [],
          completedSteps: [],
          ...stored
        };
      }
    } catch (_error) {}

    return {
      sessionId: createSessionId(),
      startedAt: null,
      sent: {},
      viewedSteps: [],
      completedSteps: []
    };
  }

  function saveAnalyticsState(key, state) {
    try {
      sessionStorage.setItem(key, JSON.stringify(state));
    } catch (_error) {}
  }

  function initQuiz(root) {
    if (root.dataset.quizInitialized === 'true') return;
    root.dataset.quizInitialized = 'true';

    const form = root.querySelector('[data-quiz-form]');
    const steps = Array.from(root.querySelectorAll('[data-quiz-step]'));
    const panel = root.querySelector('.velisse-quiz__panel');
    const cover = root.querySelector('[data-quiz-cover]');
    const experience = root.querySelector('[data-quiz-experience]');
    const startButton = root.querySelector('[data-quiz-start]');
    const closeButton = root.querySelector('[data-quiz-close]');
    const progress = root.querySelector('[data-quiz-progress]');
    const progressFill = root.querySelector('[data-quiz-progress-fill]');
    const backButton = root.querySelector('[data-quiz-back]');
    const nextButton = root.querySelector('[data-quiz-next]');
    const complete = root.querySelector('[data-quiz-complete]');
    const restartButton = root.querySelector('[data-quiz-restart]');
    const saveError = root.querySelector('[data-quiz-save-error]');
    const quizType = root.dataset.quizType || 'subscription';
    const quizVersion = root.dataset.quizVersion || '1';
    const checkoutUrl = root.dataset.checkoutUrl || '';
    const answerStorageKey = `${STORAGE_KEY_PREFIX}:${quizType}:v${quizVersion}`;
    const analyticsStorageKey = `${ANALYTICS_STORAGE_PREFIX}:${quizType}:v${quizVersion}`;
    const answers = loadAnswers(answerStorageKey);
    const analyticsState = loadAnalyticsState(analyticsStorageKey);
    let current = 0;
    let isTransitioning = false;
    let autoAdvanceTimer = null;

    if (!form || !steps.length || !panel || !cover || !experience) return;

    function elapsedSeconds() {
      if (!analyticsState.startedAt) return 0;
      return Math.max(0, Math.round((Date.now() - analyticsState.startedAt) / 1000));
    }

    function publishAnalyticsEvent(name, detail = {}) {
      const payload = {
        quiz_type: quizType,
        quiz_version: quizVersion,
        quiz_session_id: analyticsState.sessionId,
        ...detail
      };

      document.dispatchEvent(new CustomEvent('velisse:quiz-analytics', {
        detail: { name, ...payload }
      }));

      try {
        const publication = window.Shopify?.analytics?.publish?.(`velisse_quiz:${name}`, payload);
        if (publication?.catch) publication.catch(() => {});
      } catch (_error) {}
    }

    async function saveCustomerProfile() {
      const quizAnswers = quizType === 'gift'
        ? {
            recipient_name: answers.recipient_name,
            age: answers.age,
            skin_tone: answers.skin_tone,
            hair: answers.hair,
            note: answers.note || ''
          }
        : {
            age: answers.age,
            categories: answers.categories,
            goal: answers.goal,
            hair: answers.hair,
            plan: answers.plan,
            skin_tone: answers.skin_tone,
            skin_type: answers.skin_type
          };

      const response = await fetch('/apps/velisse-quiz', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: answers.email,
          quiz_session_id: analyticsState.sessionId,
          quiz_type: quizType,
          quiz_version: quizVersion,
          ...quizAnswers
        })
      });

      if (!response.ok) throw new Error(`Quiz save failed (${response.status})`);
      const payload = await response.json();
      if (!payload?.ok) throw new Error('Quiz save failed');
    }

    function trackOnce(key, name, detail = {}) {
      if (analyticsState.sent[key]) return;
      analyticsState.sent[key] = true;
      saveAnalyticsState(analyticsStorageKey, analyticsState);
      publishAnalyticsEvent(name, detail);
    }

    function stepDetails(step) {
      const stepIndex = steps.indexOf(step);
      return {
        step_number: stepIndex + 1,
        step_key: step.dataset.key,
        total_steps: steps.length,
        elapsed_seconds: elapsedSeconds()
      };
    }

    function trackStepViewed(step) {
      if (!analyticsState.startedAt) return;
      const key = step.dataset.key;
      if (analyticsState.viewedSteps.includes(key)) return;
      analyticsState.viewedSteps.push(key);
      saveAnalyticsState(analyticsStorageKey, analyticsState);
      publishAnalyticsEvent('step_viewed', stepDetails(step));
    }

    function trackStepCompleted(step) {
      const key = step.dataset.key;
      if (analyticsState.completedSteps.includes(key)) return;
      analyticsState.completedSteps.push(key);
      saveAnalyticsState(analyticsStorageKey, analyticsState);
      publishAnalyticsEvent('step_completed', stepDetails(step));
    }

    function openQuiz() {
      if (isTransitioning) return;
      isTransitioning = true;
      if (startButton) startButton.disabled = true;
      if (!analyticsState.startedAt) {
        analyticsState.startedAt = Date.now();
        saveAnalyticsState(analyticsStorageKey, analyticsState);
      }
      trackOnce('started', 'started', {
        total_steps: steps.length
      });
      cover.classList.add('is-leaving');

      afterStepAnimation(cover, 240, () => {
        cover.hidden = true;
        cover.classList.remove('is-leaving');

        if (window.matchMedia('(max-width: 749px)').matches) {
          panel.classList.add('is-fullscreen');
          document.body.classList.add('velisse-quiz-lock');
        }

        experience.hidden = false;
        experience.classList.add('is-entering');
        renderStep(current, false);

        afterStepAnimation(experience, 420, () => {
          if (experience.hidden) return;
          experience.classList.remove('is-entering');
          isTransitioning = false;
          if (startButton) startButton.disabled = false;
          const question = currentStep().querySelector('.velisse-quiz__question');
          question?.setAttribute('tabindex', '-1');
          question?.focus({ preventScroll: true });
        });
      });
    }

    function closeQuiz() {
      publishAnalyticsEvent('closed', {
        step_number: current + 1,
        step_key: currentStep().dataset.key,
        elapsed_seconds: elapsedSeconds()
      });
      isTransitioning = false;
      cancelAutoAdvance();
      cover.classList.remove('is-leaving');
      experience.classList.remove('is-entering');
      panel.classList.remove('is-fullscreen');
      document.body.classList.remove('velisse-quiz-lock');
      experience.hidden = true;
      cover.hidden = false;
      if (startButton) startButton.disabled = false;
      startButton?.focus({ preventScroll: true });
    }

    function currentStep() {
      return steps[current];
    }

    function cancelAutoAdvance() {
      if (autoAdvanceTimer === null) return;
      window.clearTimeout(autoAdvanceTimer);
      autoAdvanceTimer = null;
    }

    function afterStepAnimation(step, fallbackDuration, callback) {
      let finished = false;
      const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 0
        : fallbackDuration;

      const finish = () => {
        if (finished) return;
        finished = true;
        step.removeEventListener('animationend', handleAnimationEnd);
        callback();
      };

      const handleAnimationEnd = (event) => {
        if (event.target === step) finish();
      };

      step.addEventListener('animationend', handleAnimationEnd);
      window.setTimeout(finish, duration === 0 ? 0 : duration + 40);
    }

    function syncStep(step) {
      const key = step.dataset.key;
      let stored = answers[key];

      if (step.dataset.type === 'multi' && stored !== undefined && !Array.isArray(stored)) {
        stored = [stored];
        answers[key] = stored;
        saveAnswers(answerStorageKey, answers);
      }

      step.querySelectorAll('[data-quiz-choice]').forEach((choice) => {
        const selected = Array.isArray(stored)
          ? stored.includes(choice.dataset.value)
          : stored === choice.dataset.value;
        choice.classList.toggle('is-selected', selected);
        choice.setAttribute('aria-pressed', String(selected));
      });

      const input = step.querySelector('[data-quiz-input]');
      if (input && stored !== undefined && input.value === '') input.value = stored;
    }

    function renderStep(index, shouldFocus, direction = '') {
      current = index;
      steps.forEach((step, stepIndex) => {
        const active = stepIndex === current;
        step.hidden = !active;
        step.classList.remove('is-active', 'is-leaving-forward', 'is-leaving-back', 'is-entering-forward', 'is-entering-back');
        if (active) step.classList.add(direction ? `is-entering-${direction}` : 'is-active');
      });

      const stepNumber = current + 1;
      const percentage = Math.round((stepNumber / steps.length) * 100);
      progress.setAttribute('aria-valuenow', String(stepNumber));
      progressFill.style.width = `${percentage}%`;
      backButton.hidden = current === 0;
      const step = currentStep();
      nextButton.hidden = false;
      nextButton.textContent = current === steps.length - 1
        ? 'Finalizar Quiz'
        : 'Continuar →';

      syncStep(step);
      trackStepViewed(step);
      step.classList.remove('is-invalid');
      step.querySelector('[data-quiz-error]')?.classList.remove('is-visible');
      if (saveError) {
        saveError.hidden = true;
        saveError.classList.remove('is-visible');
      }

      if (shouldFocus) {
        const question = step.querySelector('.velisse-quiz__question');
        question?.setAttribute('tabindex', '-1');
        question?.focus({ preventScroll: true });
        experience.scrollTop = 0;
        panel.scrollTop = 0;
      }

      if (direction) {
        afterStepAnimation(step, 440, () => {
          step.classList.remove(`is-entering-${direction}`);
          step.classList.add('is-active');
          isTransitioning = false;
          backButton.disabled = false;
          nextButton.disabled = false;
        });
      }
    }

    function showStep(index, shouldFocus = true) {
      cancelAutoAdvance();
      const target = Math.max(0, Math.min(index, steps.length - 1));
      const outgoing = currentStep();

      if (!shouldFocus || target === current || outgoing.hidden) {
        renderStep(target, shouldFocus);
        return;
      }

      if (isTransitioning) return;
      isTransitioning = true;
      const direction = target > current ? 'forward' : 'back';
      backButton.disabled = true;
      nextButton.disabled = true;
      outgoing.classList.remove('is-active');
      outgoing.classList.add(`is-leaving-${direction}`);

      afterStepAnimation(outgoing, 190, () => {
        renderStep(target, shouldFocus, direction);
      });
    }

    function validate(step) {
      const key = step.dataset.key;
      const type = step.dataset.type;
      const input = step.querySelector('[data-quiz-input]');
      const optional = step.dataset.optional === 'true';
      let valid = true;

      if (optional && input && input.value.trim() === '') {
        delete answers[key];
      } else if (optional && !input && !answers[key]) {
        delete answers[key];
      } else if (type === 'number') {
        const value = Number(input?.value);
        const minimum = Number(input?.min || 0);
        const maximum = Number(input?.max || 100);
        valid = Number.isInteger(value) && value >= minimum && value <= maximum;
        if (valid) answers[key] = value;
      } else if (type === 'email') {
        valid = Boolean(input?.validity.valid && input.value.trim().length > 3);
        if (valid) answers[key] = input.value.trim();
      } else if (type === 'text') {
        const value = input?.value.trim() || '';
        valid = value.length > 0;
        if (valid) answers[key] = value;
      } else if (type === 'text-optional') {
        answers[key] = input?.value.trim() || '';
      } else if (type === 'multi') {
        valid = Array.isArray(answers[key]) && answers[key].length >= Number(step.dataset.min || 1);
      } else {
        valid = Boolean(answers[key]);
      }

      step.querySelector('[data-quiz-error]')?.classList.toggle('is-visible', !valid);
      if (!valid) {
        publishAnalyticsEvent('validation_error', stepDetails(step));
        step.classList.remove('is-invalid');
        window.requestAnimationFrame(() => step.classList.add('is-invalid'));
        input?.focus({ preventScroll: true });
      }
      if (valid) saveAnswers(answerStorageKey, answers);
      return valid;
    }

    root.querySelectorAll('[data-quiz-choice]').forEach((choice) => {
      choice.setAttribute('aria-pressed', 'false');
      choice.addEventListener('click', () => {
        if (isTransitioning) return;
        const step = choice.closest('[data-quiz-step]');
        const key = step.dataset.key;

        if (step.dataset.type === 'multi') {
          const selected = new Set(Array.isArray(answers[key]) ? answers[key] : []);
          if (selected.has(choice.dataset.value)) selected.delete(choice.dataset.value);
          else selected.add(choice.dataset.value);
          answers[key] = Array.from(selected);
        } else {
          answers[key] = choice.dataset.value;
          if (choice.dataset.planUrl !== undefined) answers.plan_url = choice.dataset.planUrl;
        }

        saveAnswers(answerStorageKey, answers);
        syncStep(step);
        step.querySelector('[data-quiz-error]')?.classList.remove('is-visible');

        if (key === 'plan') {
          publishAnalyticsEvent('plan_selected', {
            plan: choice.dataset.value,
            elapsed_seconds: elapsedSeconds()
          });
        }

        if (step.dataset.type === 'single' && step === currentStep() && current < steps.length - 1) {
          trackStepCompleted(step);
          cancelAutoAdvance();
          autoAdvanceTimer = window.setTimeout(() => {
            autoAdvanceTimer = null;
            if (step === currentStep() && !isTransitioning) showStep(current + 1);
          }, 220);
        }
      });
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (isTransitioning) return;
      if (!validate(currentStep())) return;
      trackStepCompleted(currentStep());

      if (current < steps.length - 1) {
        showStep(current + 1);
        return;
      }

      document.dispatchEvent(new CustomEvent('velisse:quiz-complete', {
        detail: { ...answers }
      }));

      trackOnce('completed', 'completed', {
        plan: answers.plan || '',
        elapsed_seconds: elapsedSeconds(),
        total_steps: steps.length
      });

      const redirectUrl = answers.plan_url || checkoutUrl;
      if (redirectUrl) {
        nextButton.classList.add('is-loading');
        nextButton.textContent = 'Guardando…';
        nextButton.disabled = true;
        nextButton.setAttribute('aria-busy', 'true');
        form.setAttribute('aria-busy', 'true');

        try {
          await saveCustomerProfile();
        } catch (error) {
          console.error(error);
          nextButton.classList.remove('is-loading');
          nextButton.textContent = 'Finalizar Quiz';
          nextButton.disabled = false;
          nextButton.removeAttribute('aria-busy');
          form.removeAttribute('aria-busy');
          if (saveError) {
            saveError.hidden = false;
            saveError.classList.add('is-visible');
          }
          return;
        }

        trackOnce('checkout_redirected', 'checkout_redirected', {
          plan: answers.plan || (quizType === 'gift' ? 'Regalo único' : ''),
          elapsed_seconds: elapsedSeconds()
        });
        nextButton.textContent = 'Redirigiendo…';

        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => window.location.assign(redirectUrl));
        });
        return;
      }

      form.hidden = true;
      progress.hidden = true;
      complete.hidden = false;
    });

    backButton.addEventListener('click', () => {
      if (!isTransitioning) showStep(current - 1);
    });

    restartButton.addEventListener('click', () => {
      publishAnalyticsEvent('restarted', {
        elapsed_seconds: elapsedSeconds()
      });
      Object.keys(answers).forEach((key) => delete answers[key]);
      clearAnswers(answerStorageKey);
      root.querySelectorAll('[data-quiz-input]').forEach((input) => { input.value = ''; });
      progress.hidden = false;
      complete.hidden = true;
      form.hidden = false;
      showStep(0);
    });

    startButton?.addEventListener('click', openQuiz);
    closeButton?.addEventListener('click', closeQuiz);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && panel.classList.contains('is-fullscreen')) closeQuiz();
    });
    window.addEventListener('pageshow', () => {
      nextButton.classList.remove('is-loading');
      nextButton.disabled = false;
      nextButton.removeAttribute('aria-busy');
      form.removeAttribute('aria-busy');
      nextButton.textContent = current === steps.length - 1
        ? 'Finalizar Quiz'
        : 'Continuar →';
    });

    steps.forEach(syncStep);
    trackOnce('viewed', 'viewed', {
      total_steps: steps.length
    });
    showStep(0, false);
  }

  function initDelivery(root) {
    if (root.dataset.deliveryInitialized === 'true') return;
    root.dataset.deliveryInitialized = 'true';

    const editionElement = root.querySelector('[data-delivery-edition]');
    const shippingElement = root.querySelector('[data-delivery-shipping]');
    const rangeElement = root.querySelector('[data-delivery-range]');
    if (!editionElement || !shippingElement || !rangeElement) return;

    const config = {
      cutoffDay: Number(root.dataset.cutoffDay || 23),
      timeZone: root.dataset.timeZone || 'America/Mexico_City',
      zoneMode: root.dataset.zoneMode || 'far'
    };

    const datePartsFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: config.timeZone,
      weekday: 'short',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23'
    });
    const offsetFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: config.timeZone,
      timeZoneName: 'shortOffset',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    });

    function timeZoneParts(date = new Date()) {
      const values = {};
      datePartsFormatter.formatToParts(date).forEach((part) => {
        if (part.type !== 'literal') values[part.type] = part.value;
      });
      return {
        weekday: values.weekday,
        year: Number(values.year),
        month: Number(values.month),
        day: Number(values.day)
      };
    }

    function offsetMinutes(date) {
      const label = offsetFormatter.formatToParts(date).find((part) => part.type === 'timeZoneName')?.value || 'GMT';
      const match = label.match(/GMT([+-]\d{1,2})(?::(\d{2}))?/);
      if (!match) return 0;
      const hours = Number.parseInt(match[1], 10);
      const minutes = match[2] ? Number.parseInt(match[2], 10) : 0;
      return (hours * 60) + (hours < 0 ? -minutes : minutes);
    }

    function localToUtc(year, month, day, hour, minute, second) {
      const base = Date.UTC(year, month - 1, day, hour, minute, second);
      const first = new Date(base - offsetMinutes(new Date(base)) * 60000);
      return new Date(base - offsetMinutes(first) * 60000);
    }

    function addDays(date, days) {
      return new Date(date.getTime() + days * 86400000);
    }

    function weekdayIndex(date) {
      const weekday = new Intl.DateTimeFormat('en-US', {
        timeZone: config.timeZone,
        weekday: 'short'
      }).format(date);
      return { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[weekday] ?? 0;
    }

    function addCalendarDay(year, month, day) {
      const nextNoon = addDays(localToUtc(year, month, day, 12, 0, 0), 1);
      const parts = timeZoneParts(nextNoon);
      return { year: parts.year, month: parts.month, day: parts.day };
    }

    function addDeliveryDays(year, month, day, amount) {
      let current = { year, month, day };
      let added = 0;
      while (added < amount) {
        current = addCalendarDay(current.year, current.month, current.day);
        const midnight = localToUtc(current.year, current.month, current.day, 0, 0, 0);
        if (weekdayIndex(midnight) !== 0) added += 1;
      }
      return localToUtc(current.year, current.month, current.day, 0, 0, 0);
    }

    function remainingLabel(target) {
      const secondsTotal = Math.max(0, Math.floor((target - new Date()) / 1000));
      const days = Math.floor(secondsTotal / 86400);
      const hours = Math.floor((secondsTotal % 86400) / 3600);
      const minutes = Math.floor((secondsTotal % 3600) / 60);
      const seconds = secondsTotal % 60;
      if (days > 0) return `${days} días y ${hours} horas`;
      if (hours > 0) return `${hours} horas y ${minutes} min`;
      if (minutes > 0) return `${minutes} min y ${seconds} seg`;
      return `${seconds} seg`;
    }

    function dateRange(start, end) {
      const first = timeZoneParts(start);
      const last = timeZoneParts(end);
      if (first.month === last.month) {
        return `Te llega entre el <strong>${first.day}</strong> y el <strong>${last.day} de ${MONTH_NAMES[first.month - 1]}</strong>`;
      }
      return `Te llega entre el <strong>${first.day} de ${MONTH_NAMES[first.month - 1]}</strong> y el <strong>${last.day} de ${MONTH_NAMES[last.month - 1]}</strong>`;
    }

    function editionDeadline() {
      const now = timeZoneParts();
      let year = now.year;
      let month = now.month;
      if (now.day > config.cutoffDay) {
        month += 1;
        if (month > 12) {
          month = 1;
          year += 1;
        }
      }
      return {
        date: localToUtc(year, month, config.cutoffDay, 23, 59, 59),
        monthIndex: month - 1
      };
    }

    function shippingState(zone) {
      const now = new Date();
      const parts = timeZoneParts(now);
      const weekday = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[parts.weekday] ?? 0;
      const saturday = weekday === 6;
      const sunday = weekday === 0;
      const midnight = localToUtc(parts.year, parts.month, parts.day, 0, 0, 0);
      let shipsToday = false;
      let countdownTarget;

      if (sunday) {
        countdownTarget = addDays(midnight, 1);
      } else {
        const cutoff = localToUtc(parts.year, parts.month, parts.day, saturday ? 11 : 15, 0, 0);
        if (now < cutoff) {
          shipsToday = true;
          countdownTarget = cutoff;
        } else {
          countdownTarget = addDays(midnight, saturday ? 2 : 1);
        }
      }

      const near = zone === 'near';
      const minimumDays = near ? (shipsToday ? 2 : 3) : (shipsToday ? 3 : 4);
      const maximumDays = near ? (shipsToday ? 4 : 5) : (shipsToday ? 5 : 6);
      let shippingPrefix = shipsToday ? '<strong>Se envía hoy</strong>' : '<strong>Se envía mañana</strong>';
      if (saturday && !shipsToday) shippingPrefix = '<strong>Se envía el lunes</strong>';

      return {
        countdownTarget,
        shippingPrefix,
        start: addDeliveryDays(parts.year, parts.month, parts.day, minimumDays),
        end: addDeliveryDays(parts.year, parts.month, parts.day, maximumDays)
      };
    }

    function detectZone() {
      if (config.zoneMode !== 'automatic') return Promise.resolve(config.zoneMode);
      return fetch('https://ipapi.co/json/')
        .then((response) => response.ok ? response.json() : Promise.reject(new Error('Location unavailable')))
        .then((data) => {
          if (data?.country_code !== 'MX') return 'far';
          const normalize = (value) => (value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          const region = normalize(data.region);
          const city = normalize(data.city);
          const code = normalize(data.region_code);
          const near = code === 'nle' || code === 'cmx' || code === 'dif'
            || region.includes('nuevo leon') || region.includes('ciudad de mexico')
            || city.includes('monterrey') || city.includes('ciudad de mexico');
          return near ? 'near' : 'far';
        })
        .catch(() => 'far');
    }

    detectZone().then((zone) => {
      let shipping = shippingState(zone);

      function render() {
        const edition = editionDeadline();
        if (shipping.countdownTarget <= new Date()) shipping = shippingState(zone);

        editionElement.innerHTML = `En <strong>${remainingLabel(edition.date)}</strong> vence la <span class="velisse-quiz__delivery-highlight">edición ${MONTHS_UPPER[edition.monthIndex]}</span> <span class="velisse-quiz__delivery-muted">(o al agotar cupos).</span>`;
        shippingElement.innerHTML = `${shipping.shippingPrefix} si pides en… <span class="velisse-quiz__delivery-time">${remainingLabel(shipping.countdownTarget)}</span>`;
        rangeElement.innerHTML = dateRange(shipping.start, shipping.end);
      }

      render();
      root._velisseDeliveryTimer = window.setInterval(render, 1000);
    });
  }

  function init(root) {
    initQuiz(root);
    initDelivery(root);
  }

  function initAll(scope = document) {
    if (scope.matches?.('[data-velisse-quiz]')) init(scope);
    scope.querySelectorAll('[data-velisse-quiz]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-velisse-quiz]');
    if (root?._velisseDeliveryTimer) window.clearInterval(root._velisseDeliveryTimer);
    if (root?.querySelector('.velisse-quiz__panel.is-fullscreen')) {
      document.body.classList.remove('velisse-quiz-lock');
    }
  });
})();
